import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag, isNullOrEmpty } from "../../../../helpers/utils/Utils";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import AddCronExp from "../modal/AddCronExp";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { useParams } from "react-router-dom";
import { useUpdateConfigurationMutation } from "../collector";
import { IoIosArrowForward } from "react-icons/io";
import { FaCirclePlus, FaTrash } from "react-icons/fa6";

interface FormProps {
    enums: any;
    undoAction?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    submitBtnText: string;
    obj: any;
    requirementsForInstrument?: any[];
    item?: string;
    index?: any;
    isReadOnly?: any
}

export const ConfigurationTabDetail: React.FC<FormProps> = ({
    enums,
    obj,
    index,
    isReadOnly
}) => {

    const { collectorId } = useParams();
    const [collectorsData, setCollectorsData] = useState<any>({});
    const [updateSchedule, setUpdateSchedule] = useState("");
    const [showCronExpModal, setShowCronExpModal] = useState(false);
    const [updateCronIndex, setUpdateCronIndex] = useState(null);
    const [schedule, setSchedule] = useState("Hourly");
    const [configBtn, isConfigBtn] = useState(false);
    const [showPasswordRemoveModal, setShowPasswordRemoveModal] = useState(false);
    const [isReadOnlyConfigPassword, setIsReadOnlyConfigPassword] = useState(true);

    const [showButtons, setShowButtons] = useState(false);
    let conObj = {
        formatImplName: "",
        formatConfigArg: "",
    };

    const [fileConfigs, setFileConfigs] = useState([conObj]);

    const [showFileConfigTab, setShowFileConfigTab] = useState<boolean[]>(new Array(fileConfigs?.length).fill(false));

    const [updateConfigurationApi, updateConfigurationpiResponse] = useUpdateConfigurationMutation();

    const handleOnChange = (
        e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null },
        index?: number // make index optional so it’s only needed when updating fileConfigs
    ) => {
        const { name, value } = "target" in e ? e.target : e;

        setCollectorsData((prevData: any) => ({
            ...prevData,
            [name]: value,
            ...(name === "state" ? { counties: [] } : {}),
        }));

        if ((name === "formatImplName" || name === "formatConfigArg") && typeof index === "number") {
            setFileConfigs((prevConfigs: any[]) => {
                const updated = [...prevConfigs];
                updated[index] = {
                    ...updated[index],
                    [name]: value,
                };
                return updated;
            });
        }

        setShowButtons(true);
    };

    const handleConfigurationPasswordRemove = () => {
        collectorsData.password = "";
        setShowPasswordRemoveModal(false);
        setIsReadOnlyConfigPassword(false)
    };

    useEffect(() => {
        if (obj) {
            const dataFileTypeOptions = createOptionListForSelectTag(enums?.["DataFileType"], "value", "constant");
            const selectedDataFileType = dataFileTypeOptions.find(
                (option: OptionType) => option.value === obj?.dataFileType
            );
            setCollectorsData({
                ...obj,
                dataFileType: selectedDataFileType || null,
            });
            setIsReadOnlyConfigPassword(obj.password)
            setSchedule(obj?.schedule)
            setFileConfigs(obj.fileConfigurations)
        }
    }, [obj]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const requestBody = {
            ...collectorsData,
            schedule: schedule.toString().endsWith("*") ? schedule.slice(0, -1) : schedule,
            dataFileType: collectorsData.dataFileType.value,
            collector: {
                id: collectorId,
            },
            fileConfigurations: fileConfigs

        }
        await handleRTKQuery(
            async () => {
                return await updateConfigurationApi({
                    requestBody,
                }).unwrap();
            },
            (isSuccess: boolean) => {
                if (isSuccess) {
                    setShowButtons(false)
                }
            }
        );

    };

    const handleAddFileConfigType = () => {
        const newTaxRate = {
            formatImplName: "",
            formatConfigArg: "",
        };
        setFileConfigs((prev: any) => [newTaxRate, ...prev]);
    };

    const toggleFileConfigTab = (index: number) => {
        const updatedShowPackageDetail = [...showFileConfigTab];
        updatedShowPackageDetail[index] = isNullOrEmpty(updatedShowPackageDetail[index]) ? true : !updatedShowPackageDetail[index];
        setShowFileConfigTab(updatedShowPackageDetail);
    }

    const handleRemoveFileConfigTab = (index: number) => {
        const updatedList = [...fileConfigs];
        updatedList.splice(index, 1);
        setFileConfigs(updatedList);
    };

    const handleToggle = (property: string) => () => {
        setCollectorsData((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
        setShowButtons(true)
    };

    return (
        <>
            <div>
                <Form
                    onSubmit={handleSubmit}
                    fields={
                        <>
                            <InputField name="url"
                                label="Url" value={collectorsData?.url || ""}
                                onChange={handleOnChange}
                                required
                                placeholder="Enter Url..."
                                disabled={isReadOnly}
                            />

                            <InputField
                                name="username"
                                label="User Name"
                                value={collectorsData?.username || ""}
                                onChange={handleOnChange}
                                placeholder="Enter User Name..." disabled={isReadOnly} />

                            <div className="col-span-12 grid grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    <label className="min-w-[100px]">Password</label>
                                </div>
                                <div className="relative col-span-9 flex items-center gap-3">
                                    <div className="w-full">
                                        <InputField
                                            placeholder="Password..."
                                            type="text"
                                            name="password"
                                            value={collectorsData?.password || ""}
                                            disabled={isReadOnlyConfigPassword}
                                            onChange={handleOnChange}
                                            hideLabel={true}
                                        />
                                    </div>
                                    <FaTrash
                                        onClick={(e) => {
                                            setShowPasswordRemoveModal(true);
                                        }}
                                        className="text-highlight-0"
                                        title="Delete Password"
                                    />
                                </div>
                            </div>

                            <SelectField
                                placeholder="Select Data File Type..."
                                value={collectorsData?.dataFileType || null}
                                options={createOptionListForSelectTag(enums?.["DataFileType"], "value", "constant")}
                                onChange={(selectedOption) => handleOnChange({ name: "dataFileType", value: selectedOption })}
                                label="Data File Type"
                                required
                                isDisabled={isReadOnly} />

                            <div className="col-span-12 grid grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    <label className="min-w-[100px]">*Schedule</label>
                                </div>

                                <div className="relative col-span-9 flex items-center gap-3">
                                    <div className="w-full">
                                        <InputField
                                            placeholder="Schedule..."
                                            type="text"
                                            name="Schedule"
                                            value={schedule}
                                            hideLabel={true}
                                            disabled={true}

                                        />
                                    </div>
                                    {!isReadOnly &&
                                        <FaCirclePlus className="text-highlight-0"
                                            onClick={(e) => {
                                                setShowCronExpModal(true);
                                                setUpdateCronIndex(null);
                                                setShowButtons(true)
                                            }}

                                            title="Add Schedule"
                                        />
                                    }

                                </div>
                            </div>
                            <InputField
                                name="retrieverImplName"
                                label="Retriever Name"
                                value={collectorsData?.retrieverImplName || ""}
                                onChange={handleOnChange}
                                placeholder="Retriever Name..."
                                disabled={isReadOnly}
                            />
                            <InputField
                                name="retrieverArg"
                                label="Retriever Args"
                                value={collectorsData?.retrieverArg || ""}
                                onChange={handleOnChange}
                                placeholder="Retriever Args..." disabled={isReadOnly} />

                            <InputField
                                name="mapperImplName"
                                label="Mapper Name"
                                value={collectorsData?.mapperImplName || ""}
                                onChange={handleOnChange}
                                placeholder="Mapper Name..."
                                required disabled={isReadOnly} />

                            <InputField
                                name="mapperConfigArg"
                                label="Mapper Args"
                                value={collectorsData?.mapperConfigArg || ""}
                                onChange={handleOnChange}
                                placeholder="Mapper Args..." disabled={isReadOnly} />

                            <InputField
                                name="persistenceImplName"
                                label="Persist Name"
                                value={collectorsData?.persistenceImplName || ""}
                                onChange={handleOnChange}
                                placeholder="Persist Name..."
                                required disabled={isReadOnly} />

                            <InputField
                                name="persistenceConfigArg"
                                label="Persist Args"
                                value={collectorsData?.persistenceConfigArg || ""}
                                onChange={handleOnChange}
                                placeholder="Persist Args..." disabled={isReadOnly} />
                            <InputField
                                name="enabled"
                                label="Enabled"
                                type="checkbox"
                                checked={collectorsData?.enabled}
                                onChange={handleToggle("enabled")} disabled={isReadOnly} />

                            <div className="col-span-12 grid grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    <label className="min-w-[100px]">*File Config</label>
                                </div>
                                <div className="relative col-span-9 flex flex-col gap-3">
                                    {fileConfigs?.map((curPerson: any, index: number) => (
                                        <div key={index} className="flex gap-2 items-start">
                                            {/* Config Block: fixed width */}
                                            <div className="w-full">
                                                <div className={`md:grid md:grid-cols-12 ${index === 0 ? "mt-3" : "mt-0"} gap-2`}>
                                                    <div className="col-span-12 mb-1 border border-surface-5">
                                                        <div
                                                            className="justify-between flex px-2 p-2 bg-surface-5 cursor-pointer items-center"
                                                            onClick={() => toggleFileConfigTab(index)}
                                                        >
                                                            <div className="flex gap-1 items-center">
                                                                <h3>{curPerson?.formatImplName || "No Format Name"}</h3>
                                                            </div>
                                                            <div className="flex gap-3">
                                                                <IoIosArrowForward
                                                                    className={
                                                                        "text-lg cursor-pointer " + (showFileConfigTab[index] ? "rotate-90" : "")
                                                                    }
                                                                />
                                                                {!isReadOnly && fileConfigs.length > 1 &&
                                                                    <FaTrash
                                                                        className={"text-highlight-0"}
                                                                        onClick={() => handleRemoveFileConfigTab(index)}
                                                                        title="Delete Seller"
                                                                    />
                                                                }

                                                            </div>
                                                        </div>

                                                        {showFileConfigTab[index] && (
                                                            <div className="w-full p-4 flex flex-col gap-3">
                                                                <InputField
                                                                    placeholder="Enter Format Name"
                                                                    label="Format Name"
                                                                    required
                                                                    name="formatImplName"
                                                                    onChange={(e) => handleOnChange(e, index)}
                                                                    value={curPerson?.formatImplName || ""}
                                                                    disabled={isReadOnly}
                                                                />
                                                                <InputField
                                                                    placeholder="Enter Format Args"
                                                                    label="Format Args"
                                                                    name="formatConfigArg"
                                                                    value={curPerson?.formatConfigArg || ""}
                                                                    onChange={(e) => handleOnChange(e, index)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Plus Icon: fixed space in second column */}
                                            <div className="w-[32px] flex justify-center pt-4">
                                                {index === 0 && !isReadOnly && (
                                                    <FaCirclePlus
                                                        className="text-highlight-0"
                                                        onClick={handleAddFileConfigType}
                                                        title="Add File Config..."
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>



                            </div>


                            {showButtons && isReadOnly === false && hasPermission(["DATARETVAL-U"]) &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    </div>
                                    <div className="relative col-span-9">
                                        <div className={"mt-4 flex"}>
                                            <ButtonTray buttons={[
                                                {
                                                    buttonProps: {
                                                        btnText: "Cancel",
                                                        onClick: () => setShowButtons(false),
                                                        type: "close"
                                                    },
                                                    buttonType: "button"
                                                },
                                                {
                                                    buttonProps: {
                                                        btnText: "Update",
                                                        type: "submit",
                                                        isLoading: (updateConfigurationpiResponse.isLoading),
                                                        isVisible: hasPermission(["CANNOTE-U"])
                                                    },
                                                    buttonType: "button"
                                                }
                                            ]} align="center" gap={2} />
                                        </div>
                                    </div>
                                </div>
                            }


                        </>
                    }


                />
                {
                    showCronExpModal &&
                    <AddCronExp show={showCronExpModal} setShow={setShowCronExpModal}
                        schedule={schedule}
                        setSchedule={setSchedule} isConfigBtn={isConfigBtn}
                        updateCronIndex={updateCronIndex}
                        updateSchedule={updateSchedule}
                        setUpdateSchedule={setUpdateSchedule}
                    />
                }
                {
                    showPasswordRemoveModal &&
                    <ConfirmationModal
                        show={showPasswordRemoveModal}
                        setShow={setShowPasswordRemoveModal}
                        title={"Remove Current Password"}
                        bodyMessage={
                            <>
                                <p>Are you sure you want to remove the current configuration password?</p>
                                <p>Once removed, you will have the option to set a new password or leave it blank.</p>
                            </>
                        } onSubmit={handleConfigurationPasswordRemove}
                    />
                }
            </div>

        </>

    );
};
